"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Loader, CircleCheck, CircleAlert, Calendar, DollarSign, Building2, User, Mail, Phone, FileText } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={cn("flex h-12 w-full rounded-sm border border-border/40 bg-secondary/10 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary/80 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-200", className)} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={cn("flex min-h-[120px] w-full rounded-sm border border-border/40 bg-secondary/10 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary/80 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-200 resize-y", className)} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
function FundingRequestContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center border border-border/50 bg-card/30 backdrop-blur-sm rounded-sm animate-in fade-in zoom-in duration-500">
        <div className="mb-6 rounded-full bg-primary/10 p-4 border border-primary/20">
          <CircleCheck className="h-12 w-12 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-foreground mb-3">Request Received</h3>
        <p className="text-muted-foreground max-w-md mb-8 leading-relaxed">
          {successMessage || "Your funding request has been securely transmitted to our underwriting team. We will review your submission and contact you shortly."}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[200px] border-primary/30 hover:bg-primary/10 hover:text-primary">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="company_name" rules={{
        required: "Company Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider">Company Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground/40" />
                  <Input placeholder="Acme Enterprises" {...field} className="pl-10" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="contact_name" rules={{
        required: "Contact Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider">Contact Name</FormLabel>
              <FormControl>
                <div className="relative">
                  <User className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground/40" />
                  <Input placeholder="Jane Doe" {...field} className="pl-10" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider">Email Address</FormLabel>
              <FormControl>
                <div className="relative">
                  <Mail className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground/40" />
                  <Input type="email" placeholder="jane.doe@example.com" {...field} className="pl-10" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider">Phone Number</FormLabel>
              <FormControl>
                <div className="relative">
                  <Phone className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground/40" />
                  <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="pl-10" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="funding_amount" rules={{
      required: "Funding amount is required",
      min: {
        value: 10000,
        message: "Minimum funding amount is $10,000"
      },
      max: {
        value: 50000000,
        message: "Maximum funding amount is $50,000,000"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider">Requested Funding Amount (USD)</FormLabel>
            <FormControl>
              <div className="relative">
                <DollarSign className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground/40" />
                <Input type="number" placeholder="250000" {...field} className="pl-10 font-mono" />
              </div>
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/60">
              Enter the amount you wish to raise (Min: $10k, Max: $50M).
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="funding_purpose" rules={{
      required: "Please select a funding purpose"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider block mb-3">Purpose of Funding</FormLabel>
            <FormControl>
              <ToggleGroup type="single" value={field.value} onValueChange={field.onChange} className="flex flex-wrap justify-start gap-3">
                {["Working Capital", "Equipment Purchase", "Expansion / Growth", "Mergers & Acquisitions", "Debt Refinancing", "Other"].map(option => <ToggleGroupItem key={option} value={option} variant="outline" className="rounded-sm border-border/40 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary hover:bg-secondary/20 hover:text-foreground transition-all duration-200 px-4 py-2 h-auto text-sm">
                    {option}
                  </ToggleGroupItem>)}
              </ToggleGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="business_description" rules={{
      required: "Business description is required",
      minLength: {
        value: 50,
        message: "Please provide at least 50 characters"
      },
      maxLength: {
        value: 1000,
        message: "Description cannot exceed 1000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider">Brief Business Description</FormLabel>
            <FormControl>
              <div className="relative">
                <FileText className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground/40" />
                <Textarea placeholder="Provide a concise overview of your business, market, and revenue model." {...field} className="pl-10 min-h-[150px]" />
              </div>
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/60 flex justify-between">
              <span>Overview of business, market, and revenue model.</span>
              <span>{field.value?.length || 0}/1000</span>
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="desired_timeline" render={({
      field
    }) => <FormItem className="max-w-md">
            <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider">Desired Funding Timeline</FormLabel>
            <FormControl>
              <div className="relative">
                <SafeHydrate><Calendar className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground/40" /></SafeHydrate>
                <Input type="date" {...field} className="pl-10" />
              </div>
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/60">
              When would you like to receive the funds?
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start gap-3">
          <CircleAlert className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-medium tracking-wide uppercase rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground transition-all duration-300 shadow-lg shadow-primary/5">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 h-5 w-5" />
            Processing Request...
          </> : "Submit Funding Request"}
      </Button>
      
      <p className="text-center text-xs text-muted-foreground/40 mt-4">
        By submitting this form, you agree to our privacy policy and terms of service. 
        Your information is encrypted and secure.
      </p>
    </Form>;
}
export default function FundingRequest() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="funding-request" className="dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1562991/img/pattern_grid_dark.png)] opacity-[0.03] pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/30 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border/30 to-transparent" />

      <div ref={ref} className={cn("max-w-3xl mx-auto transition-all duration-1000 ease-out transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-3xl md:text-4xl font-serif tracking-tight text-foreground">
            Start Your Funding Request
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto font-light">
            Institutional capital structured for your growth. Complete the form below to begin the underwriting process.
          </p>
        </div>

        <div className="bg-card/50 backdrop-blur-md border border-border/50 p-6 md:p-10 rounded-sm shadow-2xl shadow-black/20 relative">
          {}
          <div className="absolute -top-px -left-px w-4 h-4 border-t border-l border-primary/40" />
          <div className="absolute -top-px -right-px w-4 h-4 border-t border-r border-primary/40" />
          <div className="absolute -bottom-px -left-px w-4 h-4 border-b border-l border-primary/40" />
          <div className="absolute -bottom-px -right-px w-4 h-4 border-b border-r border-primary/40" />

          <FormProvider formId="funding_request_business_capital" sectionName="funding-request" defaultValues={{
          company_name: "",
          contact_name: "",
          email: "",
          phone: "",
          funding_amount: "",
          funding_purpose: "",
          business_description: "",
          desired_timeline: ""
        }} validationRules={{
          company_name: {
            required: true
          },
          contact_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          funding_amount: {
            required: true,
            min: 10000,
            max: 50000000
          },
          funding_purpose: {
            required: true
          },
          business_description: {
            required: true,
            minLength: 50,
            maxLength: 1000
          }
        }} submitText="Submit Funding Request" successMessage="Thank you. Your funding request has been received and is being reviewed by our capital structuring team.">
            <FundingRequestContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}